import React from 'react';
import { AccordionCard } from '../../Library/AccordionCard';
import { Box, Grid, Typography } from '@mui/material';
import { AnalyteData, AnalyteReport, BloodAnalyte } from '../../../types';
import { colors, theme } from '../../../utils/theme';
import {
  AnalyteResultSlider,
  AnalyteHistory,
  AnayteCardText,
} from '../BloodVitaminInsights';

export const AnalyteCard = ({
  analyte,
  analyteDataMap,
}: {
  analyte: AnalyteReport;
  analyteDataMap: Record<BloodAnalyte, AnalyteData>;
}): JSX.Element => {
  const {
    reference_range,
    role_in_body,
    optimal_level_benefit,
    food_sources,
    smart_multi_impact,
    what_is_it_description,
    impacting_factors,
    ways_to_optimize,
    is_inflammatory_marker,
    is_heavy_metal,
  } = analyteDataMap[analyte.analyte_code as BloodAnalyte] ?? {};

  return (
    <AccordionCard
      summary={
        <Grid container flexDirection="column" alignItems="flex-start">
          <Grid container item flexDirection="row" alignItems="center">
            <Typography variant="medium" fontSize={20}>
              {analyte.name}
            </Typography>
          </Grid>

          <Grid
            container
            item
            flexDirection="row"
            alignItems="center"
            sx={{ mt: 1 }}
          >
            <Typography variant="medium" fontSize={18} sx={{ opacity: 0.8 }}>
              {analyte.result_history[0].result.toFixed(1)} {analyte.unit}
            </Typography>
          </Grid>
          <Box
            sx={{
              background:
                is_inflammatory_marker || is_heavy_metal
                  ? `rgba(0, 117, 255, .7)`
                  : `rgba(23, 178, 106, .7)`,

              px: 0.5,
              borderRadius: 0.5,
              color: 'white',
              mb: 3,
              mt: 1,
            }}
          >
            <Typography
              sx={{ fontWeight: theme.typography.mono }}
              fontSize={10}
            >
              {is_inflammatory_marker
                ? 'inflammatory marker'
                : is_heavy_metal
                ? 'heavy metal'
                : analyte.test_type}
            </Typography>
          </Box>

          <AnalyteResultSlider analyte={analyte} />
        </Grid>
      }
      details={
        <Grid container flexDirection="column">
          <Box display="flex" flexDirection="row" sx={{ mb: 2 }}>
            <Typography fontSize={14} variant="light">
              Reference Range:
            </Typography>
            <Typography
              fontSize={14}
              variant="mono"
              sx={{ color: colors.primarys.RED, ml: 1 }}
            >
              {reference_range}
            </Typography>
          </Box>

          {!!role_in_body?.length && (
            <AnayteCardText
              title={`${analyte.name.toUpperCase()} ROLE IN YOUR BODY`}
              items={role_in_body}
            />
          )}

          {optimal_level_benefit && (
            <AnayteCardText
              title="BENEFITS OF OPTIMAL LEVELS"
              text={optimal_level_benefit}
            />
          )}

          {!!food_sources?.length && (
            <AnayteCardText
              title={`FOOD SOURCES OF ${analyte.name.toUpperCase()}`}
              items={food_sources}
            />
          )}

          {smart_multi_impact &&
            !(is_inflammatory_marker || is_heavy_metal) && (
              <AnayteCardText
                title=" HOW THIS IMPACTS YOUR SMART MULTIVITAMIN"
                text={smart_multi_impact}
              />
            )}

          {what_is_it_description && (
            <AnayteCardText
              title={`WHAT IS ${analyte.name.toUpperCase()}?`}
              text={what_is_it_description}
            />
          )}

          {!!impacting_factors?.length && (
            <AnayteCardText
              title={`FACTORS THAT IMPACT ${analyte.name.toUpperCase()}`}
              items={impacting_factors}
            />
          )}

          {!!ways_to_optimize?.length && (
            <AnayteCardText
              title=" WAYS TO OPTIMIZE"
              items={ways_to_optimize}
            />
          )}

          {smart_multi_impact && (is_inflammatory_marker || is_heavy_metal) && (
            <AnayteCardText
              title=" HOW THIS IMPACTS YOUR SMART MULTIVITAMIN"
              text={smart_multi_impact}
            />
          )}

          <AnalyteHistory analyte={analyte} />
        </Grid>
      }
      panelId={`${analyte.name}-blood-vitamin-insights`}
    />
  );
};
